/**
 * What the pen and the pencil look like WHILE they are being used.
 *
 * Drawn in scene coordinates inside the canvas transform layer, so it lines up
 * with the artwork at every zoom; anything that must stay a constant size on
 * screen (a node dot, the line weight) is divided by the zoom, the same trick
 * the selection chrome uses.
 *
 * This paints the draft only — the finished shape is a normal element rendered
 * by ShapeVector. Keeping the two apart is what stops a half-drawn path from
 * ever reaching the document, an undo step or an export.
 */
import { freeformCommands, commandsToPathD } from '../schema/shapeLibrary';

const BLUE = '#0d99ff';

/** Draft nodes are in SCENE units; `freeformCommands` wants the unit box. */
function draftPathD(nodes, closed) {
    if (nodes.length < 2) return '';
    const unit = nodes.map((n) => ({
        x: n.x,
        y: n.y,
        hIn: n.hIn || undefined,
        hOut: n.hOut || undefined,
    }));
    return commandsToPathD(freeformCommands({ closed, nodes: unit }, 1, 1));
}

export default function DrawingOverlay({ draft, zoom }) {
    if (!draft) return null;
    const px = (n) => n / Math.max(zoom, 0.01);

    // Pencil: the raw trail, drawn as it is sampled. No nodes are shown — there
    // are hundreds of them mid-stroke and none of them are decisions yet.
    if (draft.tool === 'pencil') {
        const trail = draft.trail || [];
        if (trail.length < 2) return null;
        const d = `M${trail.map((p) => `${p.x} ${p.y}`).join('L')}`;
        return (
            <svg
                aria-hidden
                data-export-ignore="1"
                className="absolute inset-0 pointer-events-none z-40 overflow-visible"
                style={{ width: 1, height: 1 }}
            >
                <path d={d} fill="none" stroke={BLUE} strokeWidth={px(2)} strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        );
    }

    // Pen: the committed nodes, plus a rubber band out to the pointer so the next
    // segment is visible before it is placed — Figma's preview.
    const nodes = draft.nodes || [];
    if (!nodes.length) return null;
    const committed = draftPathD(nodes, draft.closed);
    const last = nodes[nodes.length - 1];
    const rubber = draft.cursor && !draft.closed
        ? draftPathD([last, { x: draft.cursor.x, y: draft.cursor.y }], false)
        : '';
    const dot = px(3.5);
    const first = nodes[0];

    return (
        <svg
            aria-hidden
            data-export-ignore="1"
            className="absolute inset-0 pointer-events-none z-40 overflow-visible"
            style={{ width: 1, height: 1 }}
        >
            {committed && (
                <path d={committed} fill="none" stroke={BLUE} strokeWidth={px(1.5)} />
            )}
            {rubber && (
                <path d={rubber} fill="none" stroke={BLUE} strokeWidth={px(1)} strokeDasharray={`${px(4)} ${px(3)}`} />
            )}
            {/* Handle arms of the node being dragged — the only ones worth showing,
                since they are the only ones the pointer can still change. */}
            {draft.dragging != null && nodes[draft.dragging] && (() => {
                const n = nodes[draft.dragging];
                return ['hIn', 'hOut'].map((k) => (n[k] ? (
                    <g key={k}>
                        <line
                            x1={n.x}
                            y1={n.y}
                            x2={n[k][0]}
                            y2={n[k][1]}
                            stroke={BLUE}
                            strokeWidth={px(1)}
                        />
                        <circle cx={n[k][0]} cy={n[k][1]} r={dot * 0.8} fill={BLUE} />
                    </g>
                ) : null));
            })()}
            {nodes.map((n, i) => (
                <circle
                    // eslint-disable-next-line react/no-array-index-key
                    key={i}
                    cx={n.x}
                    cy={n.y}
                    r={dot}
                    fill="#fff"
                    stroke={BLUE}
                    strokeWidth={px(1.5)}
                />
            ))}
            {/* The first node grows when the pointer is near it: that is the
                target for closing the path, and it must look like one. */}
            {draft.overFirst && nodes.length > 2 && (
                <circle cx={first.x} cy={first.y} r={dot * 1.9} fill="none" stroke={BLUE} strokeWidth={px(1.5)} />
            )}
        </svg>
    );
}
